<template>
  <b-container fluid>
    <!-- Header -->
    <div class="d-flex align-items-center justify-content-between mb-4 mt-3">
      <b-button variant="outline-secondary" @click="$router.push({ name: 'superadmin.exchange-contract' })">
        <i class="fas fa-arrow-left mr-1" /> Back
      </b-button>
      <h5 class="mb-0 font-weight-bold">Exchange Contract — Vehicles</h5>
      <div />
    </div>

    <!-- Contract detail card -->
    <b-card v-if="contract" class="mb-4 shadow-sm" no-body>
      <b-card-header class="font-weight-bold bg-white">
        Contract #{{ contract.contractNumber }}
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col cols="6" md="3" class="mb-2">
            <div class="detail-label">Contract FX</div>
            <div class="detail-value">{{ contract.fx }}</div>
          </b-col>
          <b-col cols="6" md="3" class="mb-2">
            <div class="detail-label">Vehicle FX</div>
            <div class="detail-value">{{ contract.vehicleFx }}</div>
          </b-col>
          <b-col cols="6" md="3" class="mb-2">
            <div class="detail-label">Amount USD</div>
            <div class="detail-value">{{ contract.amount | currency }}</div>
          </b-col>
          <b-col cols="6" md="3" class="mb-2">
            <div class="detail-label">Amount CAD</div>
            <div class="detail-value">{{ contract.amountCad | currency }}</div>
          </b-col>
          <b-col cols="6" md="3" class="mb-2">
            <div class="detail-label">Balance USD</div>
            <div class="detail-value">{{ contract.pendingPayment | currency }}</div>
          </b-col>
          <b-col cols="6" md="3" class="mb-2">
            <div class="detail-label">Start date</div>
            <div class="detail-value">{{ contract.startDate | formatDate }}</div>
          </b-col>
          <b-col cols="6" md="3" class="mb-2">
            <div class="detail-label">End date</div>
            <div class="detail-value">{{ contract.endDate | formatDate }}</div>
          </b-col>
          <b-col cols="6" md="3" class="mb-2">
            <div class="detail-label">Missing days</div>
            <div class="detail-value">{{ contract.missingDays }}</div>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Vehicles table -->
    <div class="table-responsive">
      <b-table
        show-empty
        bordered
        small
        stacked="md"
        head-variant="light"
        :items="vehicles"
        :fields="fields"
        :busy="isLoading"
      >
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle" variant="yellow" />
          </div>
        </template>
        <template #cell(actions)="{ item }">
          <div class="text-center">
            <font-awesome-icon
              class="cursor-pointer text-primary"
              :icon="['far', 'eye']"
              @click="goToVehicle(item.vehicleId || item.id)"
            />
          </div>
        </template>
      </b-table>
    </div>

    <b-pagination
      v-if="!isLoading && totalRows > 0"
      :value="currentPage"
      :total-rows="totalRows"
      :per-page="perPage"
      class="mt-3"
      align="end"
      @change="changePage"
    />
  </b-container>
</template>

<script>
import { mapState } from "vuex";
import { GET_VEHICLES_BY_CONTRACT } from "./actions";
import { generalMixin } from "@/modules/mixin.js";

export default {
  name: "ExchangeContractVehiclesView",
  mixins: [generalMixin],
  computed: {
    ...mapState({
      vehiclesByContract: (state) =>
        state.superAdminStore.exchangeContract.vehiclesByContract,
    }),
    isLoading() {
      return this.vehiclesByContract.status === "fetching";
    },
    contract() {
      return this.vehiclesByContract.data?.contract || null;
    },
    vehicles() {
      const items = this.vehiclesByContract.data?.vehicles?.data || [];
      return items.map((item) => ({
        ...item.vehicle,
        vehicleId: item.vehicle.id,
        amountCad: item.amountCad,
      }));
    },
    totalRows() {
      return this.vehiclesByContract.data?.vehicles?.total || 0;
    },
    currentPage() {
      return this.vehiclesByContract.data?.vehicles?.currentPage || 1;
    },
    perPage() {
      return this.vehiclesByContract.data?.vehicles?.perPage || 25;
    },
  },
  data() {
    return {
      fields: [
        { key: "stockNumber", label: "Stock #", class: "text-center" },
        { key: "vinNumber", label: "VIN", class: "text-center" },
        { key: "year", label: "Year", class: "text-center" },
        { key: "make", label: "Make", class: "text-center" },
        { key: "model", label: "Model", class: "text-center" },
        { key: "color", label: "Color", class: "text-center" },
        { key: "marketPlace", label: "Marketplace", class: "text-center" },
        { key: "status", label: "Status", class: "text-center" },
        {
          key: "cost",
          label: "Cost",
          class: "text-center",
          formatter: (v) => this.$options.filters.currency(v),
        },
        {
          key: "amountCad",
          label: "Amount CAD",
          class: "text-center",
          formatter: (v) => this.$options.filters.currency(v),
        },
        { key: "actions", label: "Actions", class: "text-center" },
      ],
    };
  },
  mounted() {
    this.$store.commit("setAddFunction", null);
    this.$store.commit("setSearchFunction", null);
    this.getData();
  },
  methods: {
    getData(page = 1) {
      this.$store.dispatch(GET_VEHICLES_BY_CONTRACT, {
        id: this.$route.params.id,
        page,
      });
    },
    changePage(page) {
      this.getData(page);
    },
    goToVehicle(id) {
      const encryptedId = this.$CryptoJS.AES.encrypt(
        JSON.stringify(id),
        "Secret Passphrase"
      ).toString();
      this.$router.push({
        name: "superadmin.vehicles.unit",
        params: { id: encryptedId },
      });
    },
  },
};
</script>

<style scoped>
.detail-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #9ca3af;
}
.detail-value {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
}
</style>
